import { useState } from "react";
import { Box, Divider, ListSubheader, MenuItem } from "@mui/material";
import {
  GridToolbarContainer,
  GridToolbarFilterButton,
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-premium";
import { useNavigate } from "react-router-dom";

import { CreateFab, getListToolbarFilterButtonSx } from "../Buttons";
import { ActionMenuPremium } from "./ActionMenu";
import { AdvancedSearchToolbarProps } from "./AdvancedSearchToolbar";
import { CreateMenu, type CreateMenuSection } from "./CreateMenu";
import { Actions } from "./DataGrid";
import { DisplayPreferencesPremium } from "./DisplayPreferences";
import { DisplaySettingsMenuItem, ToolbarKebabMenu } from "./GridToolbarExport";
import { exportGridCsv } from "./gridCsvExport";
import { GridPrintMenuItem } from "./gridPrintExport";
import { SearchField } from "./SearchField";
import { isAppliedFilterItem } from "./serverFilter";

// LOCAL (R55 hand reconcile, 2026-09-27): upstream types `createMenu` as
// CreateMenuSection[] on AdvancedSearchToolbarProps itself. Local main keeps it a
// ReactNode there (the tools module passes a node, 6c65066eb), so the section
// shape is narrowed HERE — the only line that differs from gitlab/develop.
type PremiumListToolbarProps = Omit<AdvancedSearchToolbarProps, "createMenu"> & {
  createMenu?: CreateMenuSection[];
  /** A row under the toolbar, above the column headers (see PremiumListToolbar). */
  toolbarBelow?: React.ReactNode;
};

/** FILTERS reads as ACTIVE (teal) while the grid's filter model holds a row that
 *  narrows it (serverFilter.ts) — from THIS grid's own state, not the app-wide list-grid
 *  store flag, which a list screen elsewhere may have left set. THE detail-tab FILTERS:
 *  a hand-rolled Premium toolbar (the aircraft record tabs) renders this one too. The
 *  list grid's is AdvancedSearchToolbar `ListFiltersButton`. */
export function FiltersButton() {
  const apiRef = useGridApiContext();
  const model = useGridSelector(apiRef, gridFilterModelSelector);
  const active = (model?.items ?? []).some((i) => i.field !== "q" && isAppliedFilterItem(i));
  return (
    <GridToolbarFilterButton
      slotProps={{
        button: { sx: getListToolbarFilterButtonSx(active), "data-filters-active": active ? "true" : "false" } as any,
      }}
    />
  );
}

/**
 * "Download as CSV" — the grid's CSV through the ONE shared export (gridCsvExport): one
 * line per limit on a StackedLines row, no band / header rows (`skipRow`), MUI's rule for
 * the rest. Takes `hideMenu` like MUI's own item, which it replaces.
 */
function CsvExportMenuItem({
  options,
  skipRow,
  hideMenu,
}: {
  options?: { fileName?: string };
  skipRow?: (row: any) => boolean;
  hideMenu?: () => void;
}) {
  const apiRef = useGridApiContext();
  return (
    <MenuItem
      onClick={() => {
        exportGridCsv(apiRef, { fileName: options?.fileName, skipRow });
        hideMenu?.();
      }}
    >
      {apiRef.current.getLocaleText("toolbarExportCSV")}
    </MenuItem>
  );
}

/** AeronetDataGridPremium's export defaults, for a toolbar that does not pass its own. */
const DEFAULT_CSV_OPTIONS = { fileName: "Aeronet" };
const DEFAULT_PRINT_OPTIONS = { hideFooter: true, hideToolbar: true, fileName: "Aeronet" };

/**
 * The grid's own settings ⋮ at the right-hand end of a detail-tab toolbar (H233).
 *
 * Aaron 2026-09-30, on the flight record's Legs tab: "add to all the premium data grids
 * with settings for that data grid". It is the list toolbar's ⋮ (AdvancedSearchToolbar
 * `GridToolbarExport`) item for item — Export: CSV · Print; Settings: Display, then any
 * `settingsMenuExtras` — built on the PREMIUM grid's own API. The list's kebab reads the
 * Pro grid context, which Premium does not provide (BP-80), so it cannot be reused
 * as-is; the menu shell (`ToolbarKebabMenu`) and the Display item are shared instead.
 *
 * Nothing here persists: CSV / Print act on the rows the grid holds (the current
 * server page — the same as a list grid without an `exportView`), and Display applies
 * live and says it is not saved (DataGridPremium's layout persistence is a TODO).
 * CSV is the shared export (gridCsvExport, Aaron 2026-10-09 "Fix export first"): a
 * StackedLines row writes one line per limit, and `exportSkipRow` names the rows that
 * are bands / headers, not records. Print is MUI's: the grid as drawn.
 *
 * The ONE implementation, with its Display dialog. PremiumListToolbar ends with it; a tab
 * whose toolbar is its own (a raw DataGridPremium's `slots.toolbar` — the aircraft
 * Components, Inspections, ADs / SBs, Modifications and Jobs tabs) renders it as that
 * toolbar's LAST child and adds nothing else (Aaron 2026-10-09: "all aircraft tabs with line
 * items should have settings menu"). It reads the Premium grid's context, so it must sit
 * inside the grid's toolbar slot.
 *
 * The Display dialog is open/closed here unless the caller drives it (`displayOpen` +
 * `onDisplayOpenChange`) — PremiumListToolbar does, from the grid's `action` state.
 */
export function PremiumGridSettings({
  csvOptions,
  printOptions,
  settingsMenuExtras,
  exportSkipRow,
  displayOpen,
  onDisplayOpenChange,
}: {
  csvOptions?: any;
  printOptions?: any;
  settingsMenuExtras?: React.ReactNode;
  /** A row that is not a record (a band, a section header): left out of the CSV. */
  exportSkipRow?: (row: any) => boolean;
  displayOpen?: boolean;
  onDisplayOpenChange?: (open: boolean) => void;
}) {
  const [ownDisplayOpen, setOwnDisplayOpen] = useState(false);
  const open = displayOpen ?? ownDisplayOpen;
  const setOpen = onDisplayOpenChange ?? setOwnDisplayOpen;
  return (
    <>
      <ToolbarKebabMenu label="Grid settings">
        <ListSubheader className="list-subheader-item">Export</ListSubheader>
        <CsvExportMenuItem options={{ ...DEFAULT_CSV_OPTIONS, ...csvOptions }} skipRow={exportSkipRow} />
        <GridPrintMenuItem options={{ ...DEFAULT_PRINT_OPTIONS, ...printOptions }} />
        <Divider />
        <ListSubheader className="list-subheader-item">Settings</ListSubheader>
        <DisplaySettingsMenuItem onOpen={() => setOpen(true)} />
        {settingsMenuExtras}
      </ToolbarKebabMenu>
      {open ? <DisplayPreferencesPremium open onClose={() => setOpen(false)} /> : null}
    </>
  );
}

/**
 * The detail-tab toolbar for AeronetDataGridPremium (BP-80).
 *
 * (+) · ACTIONS always (each when it has something to do). With `enableFilter`
 * the row is the list grid's LEFT group — (+) · ACTIONS · Find · FILTERS, the
 * order AdvancedSearchToolbar renders — so a record tab reads as the same
 * screen family as a list (Aaron 2026-09-28, the F&D Flights tab: a lone (+)
 * above the grid, "not following patterns"). Find sends the text to `data` as
 * `q`; FILTERS writes the grid's filter model, which reaches `data` as
 * `filter`. The grid is server-mode, so a caller holding its rows whole must
 * apply both itself — `applyClientGridParams` (./clientGridParams).
 *
 * Off by default: a tab that turns it on owes a `data` that honours `q` and
 * `filter`, otherwise the controls are dead.
 *
 * `toolbarExtras` (which DataGridPremium already forwards) renders after a
 * spacer at the right-hand end — AdvancedSearchToolbar's slot, so a record tab
 * reads (+) · ACTIONS · Find · FILTERS · spacer · extras, the list order minus
 * the list-only SEARCH / export. BP-22's scope select lives here (the Log Book's
 * "Filter", H108), and the 2026-08-29 rule's day selector ("left of search", top
 * right). Opt-in: a tab that passes nothing renders exactly as before.
 *
 * The row always ENDS with the grid's settings ⋮ (H233) — after the spacer and any
 * extras, where the list toolbar puts its ⋮. So every record tab's toolbar reads
 * (+) · ACTIONS · Find · FILTERS · spacer · extras · ⋮. Its Settings → Display opens
 * the Premium Display dialog; module-level entries ride `settingsMenuExtras`.
 *
 * `toolbarBelow` is a second row under the toolbar, above the column headers, where the
 * list toolbar puts its Quick Filters (Pattern 180): the tab's own controls that act on
 * the grid as a whole (the Log Book entry's Lines tab: Task Type, Task Status, the
 * fields as chips and GENERATE LINES, Shelley's layout 2026-10-20). Opt-in.
 */
export function PremiumListToolbar(props: PremiumListToolbarProps) {
  const {
    actions,
    enableCreate,
    enableFilter,
    createMenu,
    onCreate,
    onChange,
    clearSearch,
    value,
    quickSearchItems,
    setAction,
    setSelected,
    setSelectionModel,
    toolbarExtras,
    settingsMenuExtras,
    action,
    csvOptions,
    printOptions,
    toolbarBelow,
  } = props;
  const navigate = useNavigate();

  return (
    <>
    <GridToolbarContainer>
      {enableCreate ? (
        createMenu && createMenu.length > 0 ? (
          <CreateMenu sections={createMenu} />
        ) : (
          <CreateFab
            onClick={() => (onCreate ? onCreate() : navigate("create"))}
            size="small"
          />
        )
      ) : null}
      <ActionMenuPremium
        actions={actions}
        setAction={setAction}
        setSelected={setSelected}
        setSelectionModel={setSelectionModel}
      />
      {enableFilter ? (
        <>
          <SearchField
            value={value}
            onChange={onChange}
            clearSearch={clearSearch}
            items={quickSearchItems}
          />
          <Box data-testid="premium-toolbar-filters">
            <FiltersButton />
          </Box>
        </>
      ) : null}
      <Box sx={{ flex: 1 }} />
      {toolbarExtras ? (
        <Box
          sx={{ display: "flex", alignItems: "center", mr: 1 }}
          data-testid="premium-toolbar-extras"
        >
          {toolbarExtras}
        </Box>
      ) : null}
      <PremiumGridSettings
        csvOptions={csvOptions}
        printOptions={printOptions}
        settingsMenuExtras={settingsMenuExtras}
        displayOpen={action === Actions.Settings}
        onDisplayOpenChange={(open) => setAction(open ? Actions.Settings : Actions.None)}
      />
    </GridToolbarContainer>
    {toolbarBelow ? (
      <Box
        data-testid="premium-toolbar-below"
        sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1, pb: 2 }}
      >
        {toolbarBelow}
      </Box>
    ) : null}
    </>
  );
}
